import { SegmentType } from "../types";
import { prepareCenterlineData, interpolateAt, } from "../site/mid-path/mid-path-interpolation";
/**
 * Line Profile Geometry Utilities
 *
 * This module provides geometric calculation utilities used by the 2D
 * Line Profile view (vertical alignment/profile). It includes logic for
 * handling slope-break insertions and updating outlines.
 *
 * Main features:
 * - Calculation of key-point indices (BP/EP/IP/BC/EC) using centerline interpolation
 * - Insertion and handling of slope-break points
 * - Recalculation of indices after outline modifications
 *
 * Note: This module uses mid-path-interpolation.ts for accurate distance calculations
 * and nearest-point searches to avoid code duplication.
 */
// ============================================================================
// Constants
// ============================================================================
/** Distance tolerance for point matching in meters */
export const DISTANCE_TOLERANCE = 0.001;
/** Height tolerance for point matching in meters */
export const HEIGHT_TOLERANCE = 0.001;
/** Percentage threshold to skip slope breaks at endpoints (0% and 100%) */
export const ENDPOINT_SLOPE_BREAK_THRESHOLD = {
    MIN: 0,
    MAX: 1,
};
// ============================================================================
// Utility Functions
// ============================================================================
/**
 * Find the nearest point index at a given target distance along a path
 * Uses centerline interpolation for accurate distance-based search
 *
 * @param pathPoints Array of path point information from pathInfo
 * @param targetDistance Target distance from the start of the path
 * @returns Index of the nearest point
 */
function findNearestIndexAtDistance(pathPoints, targetDistance) {
    if (pathPoints.length === 0)
        return 0;
    if (pathPoints.length === 1)
        return 0;
    const centerlineData = prepareCenterlineData(pathPoints);
    const result = interpolateAt(centerlineData, targetDistance);
    // Find the nearest actual point index to the interpolated position
    const { segmentIndex, localT } = result;
    // If we're closer to the start of the segment, return that index
    // Otherwise return the next index
    return localT < 0.5
        ? segmentIndex
        : Math.min(segmentIndex + 1, pathPoints.length - 1);
}
/**
 * Calculate key point indices for path-based centerlines
 * Identifies BP (Beginning Point), EP (End Point), IP (Intermediate Points), and BC/EC (Curve points)
 *
 * Uses centerline interpolation for accurate distance-based calculations.
 *
 * @param pathPoints Array of path point information from pathInfo
 * @param segments Array of path segments
 * @returns Object containing keyPointIndices and segmentStartDistances
 */
export function calculateKeyPointIndices(pathPoints, segments) {
    const keyPointIndices = [];
    // Add BP (first point)
    if (pathPoints.length > 0) {
        keyPointIndices.push(0);
    }
    // Add EP (last point)
    if (pathPoints.length > 1) {
        keyPointIndices.push(pathPoints.length - 1);
    }
    // Process segments if available
    if (segments.length === 0) {
        return { keyPointIndices, segmentStartDistances: [] };
    }
    // Calculate cumulative segment distances
    let cumulativeDistance = 0;
    const segmentStartDistances = [0];
    for (const segment of segments) {
        cumulativeDistance += segment.length;
        segmentStartDistances.push(cumulativeDistance);
    }
    // Add IP points (segment boundaries, except BP and EP)
    for (let i = 1; i < segmentStartDistances.length - 1; i++) {
        const boundaryDist = segmentStartDistances[i];
        const nearestIndex = findNearestIndexAtDistance(pathPoints, boundaryDist);
        if (!keyPointIndices.includes(nearestIndex)) {
            keyPointIndices.push(nearestIndex);
        }
    }
    // Add BC/EC points (midpoints of CURVE segments)
    for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        if (segment.type === SegmentType.CURVE) {
            const segmentStartDist = segmentStartDistances[i];
            const segmentMidDist = segmentStartDist + segment.length / 2;
            const nearestIndex = findNearestIndexAtDistance(pathPoints, segmentMidDist);
            if (!keyPointIndices.includes(nearestIndex)) {
                keyPointIndices.push(nearestIndex);
            }
        }
    }
    return { keyPointIndices, segmentStartDistances };
}
/**
 * Recalculate indices after inserting new points into an outline
 * @param originalIndices Original indices before insertion
 * @param originalOutline Original outline before insertion
 * @param newOutline New outline after insertion
 * @param distanceTolerance Tolerance for distance comparison
 * @param heightTolerance Tolerance for height comparison
 * @returns Recalculated indices in the new outline
 */
export function recalculateIndicesAfterInsertion(originalIndices, originalOutline, newOutline, distanceTolerance = DISTANCE_TOLERANCE, heightTolerance = HEIGHT_TOLERANCE) {
    return originalIndices
        .map((idx) => {
        if (idx >= originalOutline.length) {
            return -1;
        }
        const target = originalOutline[idx];
        return newOutline.findIndex((point) => Math.abs(point[0] - target[0]) < distanceTolerance &&
            Math.abs(point[1] - target[1]) < heightTolerance);
    })
        .filter((idx) => idx !== -1);
}
/**
 * Extract slope break points to insert into the outline
 * Filters out endpoint slope breaks (0% and 100%)
 *
 * @param slopeBreaks Array of slope breaks with percentage and height
 * @param totalLength Total path length
 * @returns Array of slope break points with distance and height
 */
export function extractSlopeBreakPoints(slopeBreaks, totalLength) {
    return slopeBreaks
        .filter((sb) => sb.percentage !== ENDPOINT_SLOPE_BREAK_THRESHOLD.MIN &&
        sb.percentage !== ENDPOINT_SLOPE_BREAK_THRESHOLD.MAX)
        .map((sb) => ({
        distance: totalLength * sb.percentage,
        height: sb.height,
    }));
}
/**
 * Insert slope break points into outline and recalculate indices
 * This function modifies the outline array and returns updated indices
 *
 * @param outline Original outline array (will be modified)
 * @param keyPointIndices Original key point indices
 * @param slopeBreaks Slope breaks to insert
 * @returns Object containing updated keyPointIndices and new slopeBreakIndices
 */
export function insertSlopeBreaksIntoOutline(outline, keyPointIndices, slopeBreaks) {
    // Calculate total path length from outline
    const totalLength = outline.length > 0 ? outline[outline.length - 1][0] : 0;
    // Extract slope break points to insert
    const slopeBreakPoints = extractSlopeBreakPoints(slopeBreaks, totalLength);
    if (slopeBreakPoints.length === 0) {
        return { keyPointIndices, slopeBreakIndices: [] };
    }
    // Store original key point data for later recalculation
    const keyPointData = keyPointIndices
        .filter((idx) => idx < outline.length)
        .map((idx) => ({
        distance: outline[idx][0],
        height: outline[idx][1],
    }));
    // Store original outline for recalculation
    const originalOutline = [...outline];
    // Insert slope break points into outline
    for (const sbPoint of slopeBreakPoints) {
        outline.push([sbPoint.distance, sbPoint.height]);
    }
    // Sort outline by distance to maintain correct order
    outline.sort((a, b) => a[0] - b[0]);
    // Recalculate key point indices
    const newKeyPointIndices = keyPointData
        .map((kpData) => outline.findIndex((point) => Math.abs(point[0] - kpData.distance) < DISTANCE_TOLERANCE &&
        Math.abs(point[1] - kpData.height) < HEIGHT_TOLERANCE))
        .filter((idx) => idx !== -1);
    // Find indices of slope break points in sorted outline
    const slopeBreakIndices = slopeBreakPoints
        .map((sbPoint) => outline.findIndex((point) => Math.abs(point[0] - sbPoint.distance) < DISTANCE_TOLERANCE &&
        Math.abs(point[1] - sbPoint.height) < HEIGHT_TOLERANCE))
        .filter((idx) => idx !== -1 && !newKeyPointIndices.includes(idx));
    return {
        keyPointIndices: newKeyPointIndices,
        slopeBreakIndices,
    };
}
//# sourceMappingURL=line-profile-geometry-utils.js.map